// The API contract in one pure place: server agent -> form state -> payloads.
// Plain JS so node can run agentPayload.selfcheck.mjs without a test runner.
//
// `buildDataVariables` is injected because the real grouping helper lives in a
// React component file (DataVariablesSection) that node cannot import.
import {
  layaConfigToState,
  stateToLayaConfig,
  llmConfigToState,
  validateLaya,
} from './layaLlm.js';

export const DEFAULT_INSTRUCTIONS = [
  {
    id: 'seed-faq-flow',
    title: 'FAQ Flow',
    body: 'Refer to the uploaded knowledge base to understand how this product helps customers and answer accordingly.',
    enabled: true,
  },
];

export const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export function hydrateInstructions(userInstruction) {
  const list = asArray(userInstruction);
  if (list.length === 0) return DEFAULT_INSTRUCTIONS;
  return list.map((item, i) =>
    typeof item === 'string'
      ? { id: `loaded-${i}`, title: `Instruction ${i + 1}`, body: item, enabled: true }
      : {
          id: `loaded-${i}`,
          title: item?.name || `Instruction ${i + 1}`,
          body: item?.description || '',
          enabled: item?.enabled !== false,
        },
  );
}

export function hydrateFollowUp(followup, followUpRules) {
  const list = asArray(followup?.length ? followup : followUpRules);
  if (list.length === 0) return { count: 1, steps: [{ waitTime: '', message: '' }], chatEndTimeout: '' };
  const steps = list.map((item) =>
    typeof item === 'string'
      ? { waitTime: '', message: item }
      : { waitTime: String(item?.time_interval || '').replace(/s$/i, ''), message: item?.message || '' },
  );
  return { count: steps.length, steps, chatEndTimeout: '' };
}

export function hydrateCompletions(completions) {
  const list = asArray(completions);
  if (list.length === 0) return [{ id: 'seed-rule-1', type: '', description: '' }];
  return list.map((item, i) => ({
    id: `loaded-rule-${i}`,
    type: item?.name || '',
    description: item?.description || '',
  }));
}

export const hydrateFunctions = (apis) =>
  asArray(apis).map((item, i) =>
    typeof item === 'string'
      ? { id: `loaded-fn-${i}`, name: item }
      : {
          id: `loaded-fn-${i}`,
          name: item?.name || item?.api_id || '',
          description: item?.description || item?.definition?.description || '',
          url: item?.definition?.url || '',
          method: item?.definition?.method || 'GET',
        },
  );

export const hydrateMcpServers = (mcpServers) =>
  asArray(mcpServers).map((s, i) => ({
    id: `loaded-mcp-${i}`,
    name: s?.name || '',
    url: s?.url || '',
    description: s?.description || '',
  }));

export function hydrateDataVariables(dataVariables, { normalizeType = (t) => t || 'string', normalizeSource = (s) => s || 'collected' } = {}) {
  return asArray(dataVariables).map((g, i) => ({
    id: `loaded-group-${i}`,
    variable_name: g?.variable_name || '',
    variable_description: g?.variable_description || '',
    variables: asArray(g?.variables).length
      ? asArray(g.variables).map((v, vi) => ({
          id: `loaded-group-${i}-var-${vi}`,
          key: v?.key || '',
          type: normalizeType(v?.type),
          source: normalizeSource(v?.source),
          default: v?.default === null || v?.default === undefined ? '' : String(v.default),
          required: Boolean(v?.required),
          description: v?.description || '',
        }))
      : [
          {
            id: `loaded-group-${i}-var-0`,
            key: '',
            type: normalizeType(),
            source: normalizeSource(),
            default: '',
            required: false,
            description: '',
          },
        ],
  }));
}

/** Server agent (or null, for a brand new one) -> the form state the page holds. */
export function hydrateAgent(agent, { agentType = 'voice', agentName, user = {}, normalizers } = {}) {
  const a = agent || {};
  return {
    agentId: a.agent_id || '',
    agentType,
    agentName: agentName || a.agent_name || 'Support Bot',
    draftId: a.id || null,
    status: a.status || 'draft',
    user,
    selectedKbId: a.knowledge_base_id || '',
    instructions: hydrateInstructions(a.user_instruction),
    functions: hydrateFunctions(a.apis || a.APIs),
    followUp: hydrateFollowUp(a.followup, a.follow_up_rules),
    dataVariables: hydrateDataVariables(a.data_variables, normalizers),
    voiceId: a.voice_id ? String(a.voice_id) : '',
    language: a.language || '',
    languageId: a.language_id ?? null,
    personaType: a.persona_type ? String(a.persona_type) : '',
    personaData: {
      greeting: a.persona_data?.greeting || '',
      do_not_say: asArray(a.persona_data?.do_not_say),
      display_name: a.persona_data?.display_name || '',
      role_description: a.persona_data?.role_description || '',
    },
    conversationFlow: a.conversational_flow || '',
    mcpServers: hydrateMcpServers(a.mcp_servers),
    humanHandoverEnabled:
      typeof a.human_handover === 'boolean' ? a.human_handover : asArray(a.human_handover).length > 0,
    completionRules: hydrateCompletions(a.completions || a.completion_type || a.completion_types),
    laya: layaConfigToState(a.laya_config, a.language),
    llm: llmConfigToState(a.llm_config),
  };
}

const AUTH_TYPE_MAP = { None: 'none', 'API Key': 'api_key', 'Bearer Token': 'bearer', 'Basic Auth': 'basic' };

export function buildApiDefinitions(functions) {
  return (functions || []).map((f) => {
    const authType = AUTH_TYPE_MAP[f.authentication] || 'none';
    const auth = { type: authType };
    if (authType !== 'none' && f.authValue) auth.value_ref = f.authValue;
    const responseMap = {};
    if (f.successPath) responseMap.success_path = f.successPath;
    if (f.errorPath) responseMap.error_path = f.errorPath;
    return {
      api_id: f.name,
      name: f.name,
      description: f.description || '',
      definition: {
        description: f.description || '',
        url: f.url || '',
        method: f.method || 'GET',
        auth,
        headers: (f.headers || []).reduce((acc, h) => {
          if (h.key) acc[h.key] = h.value;
          return acc;
        }, {}),
        params: (f.parameters || []).map((p) => ({
          name: p.name,
          type: p.type || 'string',
          location: (p.location || 'query').toLowerCase(),
          required: !!p.required,
          description: p.description || '',
        })),
        timeout: Number(f.timeout) || 10,
        retry: { max_attempts: Number(f.retryAttempts) || 1, backoff_factor: Number(f.backoffFactor) || 2 },
        ...(Object.keys(responseMap).length ? { response_map: responseMap } : {}),
      },
    };
  });
}

const botType = (agentType) => (agentType === 'voice' ? 'voice_bot' : agentType || 'voice_bot');


export const defaultFlowText = (s) => s.instructions.map((i) => i.body).join('\n') || s.agentName;

const flowText = (s) => s.conversationFlow || defaultFlowText(s);

const filledRules = (rules) => (rules || []).filter((r) => r.type?.trim?.() || r.description?.trim?.());

/** Master-list defaults */
export const usableDefaults = (list) =>
  (list || []).filter((d) => d?.name?.trim?.()).map((d) => ({ name: d.name.trim(), description: d.description || '' }));

const ruleValid = (r) => !!r.type?.trim() && /^[a-z0-9_]+$/.test(r.type.trim()) && !!r.description?.trim();
const asCompletion = (r) => ({ name: r.type, description: r.description });


export const ownCompletions = (state) =>
  state.humanHandoverEnabled ? filledRules(state.completionRules).map(asCompletion) : [];


export function flowCompletions(state) {
  const own = state.humanHandoverEnabled ? filledRules(state.completionRules).filter(ruleValid).map(asCompletion) : [];
  return own.length ? own : usableDefaults(state.defaultCompletions);
}

export function buildFlowPayload(state) {
  const s = state;
  return {
    ...(s.draftId ? { draft_id: s.draftId } : {}),
    ...(s.agentId ? { agent_id: s.agentId } : {}),
    bot_type: botType(s.agentType),
    user_instruction: s.instructions.map((i) => ({ name: i.title, description: i.body })),
    APIs: s.functions.map((f) => ({ name: f.name, description: f.description || '' })),
    integration: s.mcpServers
      .filter((m) => m.name || m.url)
      .map((m) => ({ name: m.name, description: m.description || m.url || '' })),
    followup: s.followUp.steps
      .filter((x) => x.message?.trim() && String(x.waitTime ?? '').trim())
      .map((x) => ({ time_interval: String(x.waitTime).trim(), message: x.message })),
    completion_types: flowCompletions(s),
    conversational_flow: flowText(s),
  };
}

export function buildSavePayload(state, { buildDataVariables = (g) => g } = {}) {
  const s = state;
  return {
    ...(s.agentId ? { agent_id: s.agentId } : {}),
    ...(s.draftId ? { draft_id: s.draftId } : {}),
    bot_type: botType(s.agentType),
    user_id: String(s.user?.id ?? ''),
    ...(s.user?.company_id ? { reseller_id: String(s.user.company_id) } : {}),
    agent_name: s.agentName,
    status: 'published',
    user_instruction: s.instructions.filter((i) => i.enabled).map((i) => ({ name: i.title, description: i.body })),
    APIs: buildApiDefinitions(s.functions),
    knowledge_base_id: s.selectedKbId || null,
    followup: s.followUp.steps
      .filter((x) => x.message?.trim())
      .map((x) => ({ time_interval: String(x.waitTime ?? '').trim(), message: x.message })),
    conversational_flow: flowText(s),
    human_handover: s.humanHandoverEnabled,
    completion_type: ownCompletions(s),
    language: s.language || 'en-IN',
    ...(s.languageId ? { language_id: Number(s.languageId) } : {}),
    data_variables: buildDataVariables(s.dataVariables),
    interruption_handling: true,
    ...(s.voiceId ? { voice_id: Number(s.voiceId) } : {}),
    ...(s.personaType ? { persona_type: Number(s.personaType) } : {}),
    persona_data: {
      greeting: s.personaData.greeting || '',
      do_not_say: (s.personaData.do_not_say || []).filter((r) => r && r.trim()),
      display_name: s.personaData.display_name || '',
      role_description: s.personaData.role_description || '',
    },
    third_party_integration: '',
    mcp_servers: s.mcpServers
      .filter((m) => m.name || m.url)
      .map((m) => ({ name: m.name, url: m.url, ...(m.description ? { description: m.description } : {}) })),
   
    ...(stateToLayaConfig(s.laya, s.agentName) ? { laya_config: stateToLayaConfig(s.laya, s.agentName) } : {}),
    // AI Behaviour (llm_config) is hidden for now: never sent, so the saved value is kept
  };
}

/**
 * Everything standing between this agent and Publish, in the order the user
 * meets it on screen. Each entry carries the tree node id so the button, the
 * tooltip and the inspector can all jump straight to the thing that is wrong.
 * @returns {{id: string, label: string}[]} empty = ready to publish.
 */
export function missingForPublish(state) {
  const s = state || {};
  const p = s.personaData || {};
  const personaComplete =
    !!s.personaType && !!p.greeting?.trim() && !!p.display_name?.trim() && !!p.role_description?.trim();
  const hasInstruction = (s.instructions || []).some((i) => i.enabled !== false && i.body?.trim());
  const rules = filledRules(s.completionRules);

  const out = [];
  if (!personaComplete) out.push({ id: 'persona', label: 'Persona — callers need a name, a role and a greeting.' });
  if (!s.voiceId) out.push({ id: 'voice', label: 'Pick a voice so callers hear someone.' });
  if (!hasInstruction) out.push({ id: 'instructions', label: 'Add one instruction so the agent knows its job.' });
  if (!s.selectedKbId) out.push({ id: 'kb', label: 'Attach a knowledge base to answer from.' });

  
  if (s.humanHandoverEnabled) {
    const broken = rules.find((r) => !ruleValid(r));
    if (broken) {
      out.push({
        id: 'completion',
        label: `Completion rule "${broken.type || 'untitled'}" needs a snake_case type and a description.`,
      });
    }
  }

  const halfFollowUp = (s.followUp?.steps || []).find(
    (x) => !!x.message?.trim() !== !!String(x.waitTime ?? '').trim(),
  );
  if (halfFollowUp) out.push({ id: 'followup', label: 'Each follow-up needs both a wait time and a message, or leave both empty.' });

  if (!s.flowGenerated) out.push({ id: 'flow', label: 'Generate the flow chart.' });

  // a wholly blank MCP row is simply ignored; half a row is a mistake
  const halfMcp = (s.mcpServers || []).find((m) => !!m.name?.trim() !== !!m.url?.trim());
  if (halfMcp) out.push({ id: 'mcp', label: 'Each MCP server needs both a name and a URL.' });

  const layaErrors = validateLaya(s.laya).errors;
  if (layaErrors.length) out.push({ id: 'laya', label: layaErrors[0] });

  return out;
}

/**
 * Import: a pasted/uploaded JSON export -> the same form state hydrateAgent
 * produces. Identity fields (agent_id, draft_id) are deliberately dropped so an
 * import fills a form, it never takes over another agent's row.
 * Throws an Error whose message is safe to show to the user.
 * @returns {{state: object, warnings: string[]}}
 */
export function parseAgentJson(text, options = {}) {
  let raw;
  try {
    raw = JSON.parse(String(text ?? '').trim());
  } catch {
    throw new Error('That is not valid JSON. Paste the whole file, including the outer { }.');
  }

  const agent = raw?.agent || raw?.data || raw;
  if (!agent || typeof agent !== 'object' || Array.isArray(agent)) {
    throw new Error('This JSON is not an agent. Expected an object with fields like agent_name and voice_id.');
  }

  const known = [
    'agent_name', 'user_instruction', 'knowledge_base_id', 'voice_id', 'persona_type', 'persona_data',
    'language', 'completion_type', 'completion_types', 'APIs', 'apis', 'followup', 'mcp_servers',
    'data_variables', 'laya_config', 'llm_config', 'conversational_flow', 'human_handover',
  ];
  if (!known.some((k) => k in agent)) {
    throw new Error('This JSON is not an agent. Expected an object with fields like agent_name and voice_id.');
  }

  const { agent_id, draft_id, id, status, ...rest } = agent;
  const state = hydrateAgent(rest, options);
  state.agentId = options.agentId || '';
  state.draftId = options.draftId || null;

  const warnings = [];
  if (agent_id || draft_id || id) warnings.push('The agent id in the file was ignored — this fills the form only.');
  if (!agent.voice_id) warnings.push('No voice in the file — pick one before publishing.');
  if (!agent.knowledge_base_id) warnings.push('No knowledge base in the file — attach one before publishing.');

  return { state, warnings };
}
